import { FuldmaegtigeForPersonResponse } from "../components/autocomplete/interface";
import {
  Person,
  PersonDto,
  PersonIdKort,
  PersonIdKortStatus,
} from "../interfaces/schema";
import { SortBy } from "../interfaces/table";
import { dateIsWithinInterval, periodeIsActive } from "./date";
import { orderBy } from "./orderBy";

export const extractMoedeRaetter = (person: PersonDto) => {
  return person.personPersontype
    ? person.personPersontype
        .filter((p) => [18, 19].indexOf(p.persontypeNrNavigation?.nr ?? 0) > -1)
        .filter((m) =>
          periodeIsActive(
            new Date(m.periode.fraDato),
            new Date(m.periode.tilDatoInklusiv),
          ),
        )
    : [];
};

export const extractCurrentPeronBeskikkelse = (person: PersonDto) => {
  return person.personBeskikkelse?.find((p) =>
    dateIsWithinInterval(
      new Date(),
      new Date(p.periode.fraDato),
      new Date(p.periode.tilDatoInklusiv),
    ),
  );
};

export const extractCurrentPersonFirma = (person: PersonDto) => {
  if (!person.primaerPersonFirmaRelation) return undefined;
  return person.primaerPersonFirmaRelation;
};

export const extractCurrentPersonArt = (person: PersonDto | Person) => {
  return person.personPersonart?.find((p) =>
    dateIsWithinInterval(
      new Date(),
      new Date(p.periode.fraDato),
      new Date(p.periode.tilDatoInklusiv),
    ),
  );
};

export const extractCurrentPrincipal = (person: PersonDto) => {
  return person.personPrincipalPersonFuldm?.find((pp) =>
    dateIsWithinInterval(
      new Date(),
      new Date(pp.periode.fraDato),
      new Date(pp.periode.tilDatoInklusiv),
    ),
  );
};

/**
 *
 * @param personFuldmaegtige response from FULDMAEGTIGE_FOR_PERSON_BY_ID_QUERY
 * @param date date at which to look at
 */
export const getNumberOfFuldmaegtigeAtDate = (
  personFuldmaegtige: FuldmaegtigeForPersonResponse | undefined,
  date: Date | undefined,
) => {
  if (!personFuldmaegtige || !date) return 0;
  const fuldmaegtige =
    personFuldmaegtige.person.personPrincipalPersonPrincipalNavigation;
  const startDato = new Date(date);
  const existingFuldmaegtigeOnStartDate = fuldmaegtige.filter((f) => {
    const fStartDato = new Date(f.periode.fraDato);
    const fTilDatoInklusiv = new Date(f.periode.tilDatoInklusiv);
    return dateIsWithinInterval(startDato, fStartDato, fTilDatoInklusiv);
  });
  return existingFuldmaegtigeOnStartDate.length;
};

// Manglende eller ugyldig opretDato sorteres som ældst i stedet for at give NaN — en comparator,
// der returnerer NaN, har udefineret opførsel i Array.prototype.sort.
const idKortOpretTid = (idKort: PersonIdKort) => {
  if (!idKort.opretDato) {
    return 0;
  }

  const tid = new Date(idKort.opretDato).getTime();
  return Number.isNaN(tid) ? 0 : tid;
};

const nyesteIdKortFoerst = (a: PersonIdKort, b: PersonIdKort) => {
  const forskel = idKortOpretTid(b) - idKortOpretTid(a);
  // Id som tiebreak: opretDato er nullable, og to kort oprettet i samme gemning får identisk
  // tidsstempel. Uden den ville rækkefølgen være vilkårlig i netop de tilfælde.
  return forskel !== 0 ? forskel : b.id.localeCompare(a.id);
};

/**
 * Personens fulde ID-kort-historik, nyeste bestilling først.
 *
 * Receptionen bruger historikken til at vurdere en henvendelse om et manglende kort — "har hun
 * fået et kort før?" kan ikke besvares af det aktuelle kort alene. Første element er samtidig
 * personens aktuelle kort, som handlingsmenuen "Markér ID-kort som sendt" arbejder på — så
 * listens øverste række og handlingsmenuens valg kan ikke være uenige.
 *
 * Status indgår bevidst ikke i sorteringen. Reglen var før "første med status Bestilt, ellers det
 * første i arrayet", og fallbacken slog til så snart den nyeste bestilling var ekspederet: så
 * viste badget en vilkårlig ældre bestilling med dens datoer (ADV-191). En nyere Bestilt-bestilling
 * vinder alligevel, fordi den er nyest.
 *
 * Der sorteres her, selvom serveren også leverer nyeste først: rækkefølgen på en GraphQL-liste er
 * ikke en del af kontrakten, og en person kan have flere kort, siden det unikke indeks blev
 * droppet.
 *
 * Bemærk: sorteringen læser opretDato, som derfor SKAL være med i GraphQL-selektionen. Uden den
 * falder rækkefølgen tilbage på id som tiebreak — stille og uden fejl.
 */
export const extractPersonIdKortHistorik = (
  person: PersonDto,
): PersonIdKort[] => {
  const idKortEntries = person.personIdKort;
  if (!idKortEntries?.length) {
    return [];
  }

  return [...idKortEntries].sort(nyesteIdKortFoerst);
};

/**
 * Sekretariatet kalder det "sendt", ikke "behandlet" (ADV-234) — det er kortets afsendelse, de
 * registrerer. Kun ordet skifter: status hedder fortsat Behandlet i skema, endpoint og database.
 *
 * Teksten bor her, fordi både statusbadget og Hent-eksporten skal bruge den: eksporten må ikke
 * afsløre enum-værdien, og badget og regnearket må ikke sige to forskellige ting om samme kort.
 */
export const idKortStatusTekst = (status: PersonIdKortStatus): string =>
  status === PersonIdKortStatus.Behandlet ? "Sendt" : "Bestilt";

/** Én række i ID-kort-tabellen: felterne, som kolonnerne i defaultPersonIdKortColumns slår op. */
export type IdKortTabelRaekke = Pick<
  PersonIdKort,
  "id" | "status" | "opretDato" | "ekspederingstidspunkt" | "ekspederetAf"
>;

/**
 * Historikken som tabelrækker.
 *
 * Ekspederingsfelterne nulstilles for bestillinger, der ikke er sendt: status afgør, om
 * Sendt-kolonnerne har noget at vise, uanset hvad der måtte stå i databasen. Blanke initialer
 * tælles som manglende, så "Sendt af" ikke bliver en kolonne med usynligt indhold.
 *
 * Bor her frem for i komponenten, fordi repoet ikke har nogen DOM-testopsætning: logik, der
 * ligger i en komponent, er reelt utestbar. Datoerne formateres af tabellens egen date-kolonne
 * (stringDateToPP), som også håndterer AIKAs sentinel-årstal.
 */
export const formatIdKortTabelRaekker = (
  historik: PersonIdKort[],
): IdKortTabelRaekke[] =>
  historik.map((idKort) => {
    const erSendt = idKort.status === PersonIdKortStatus.Behandlet;
    const ekspederetAf = idKort.ekspederetAf?.trim();

    return {
      id: idKort.id,
      status: idKort.status,
      opretDato: idKort.opretDato ?? null,
      ekspederingstidspunkt: erSendt
        ? (idKort.ekspederingstidspunkt ?? null)
        : null,
      ekspederetAf: erSendt && ekspederetAf ? ekspederetAf : null,
    };
  });

/**
 * Rækkerne i den rækkefølge, brugerens kolonnesortering beder om — som nabosektionerne, der
 * sorterer med orderBy, så en dansk sortering af fx initialer opfører sig ens på hele fanen.
 *
 * Id lægges til som sidste sorteringsnøgle: to bestillinger gemt i samme ombæring får identisk
 * tidsstempel, og uden et tiebreak ville deres rækkefølge kunne skifte mellem to genhentninger
 * af den samme historik.
 */
export const sortIdKortTabelRaekker = (
  raekker: IdKortTabelRaekke[],
  sortBy: SortBy[],
): IdKortTabelRaekke[] =>
  orderBy(
    raekker,
    [...sortBy.map((s) => s.field), "id"],
    [...sortBy.map((s) => s.direction), sortBy.at(-1)?.direction ?? "desc"],
  );

/**
 * Rækkerne som Hent-eksporten skal skrive: statussen bliver den tekst, skærmen viser, mens de
 * øvrige felter beholder deres navne, så kolonnekonfigurationen kan slå dem op i regnearket.
 */
export const formatIdKortTabelEksport = (raekker: IdKortTabelRaekke[]) =>
  raekker.map((raekke) => ({
    ...raekke,
    status: idKortStatusTekst(raekke.status),
  }));
